import React, { useEffect, useState } from 'react';
import { emailLogAPI } from '../api';
import EmailTemplatePreview from '../components/EmailTemplatePreview';

/**
 * AdminEmailLog — every email send attempt, calmly listed.
 *
 * Surface for the founder to verify password reset deliverability, see
 * Resend rejection codes, and audit which addresses tried forgot-password
 * against the platform. Reads from /api/admin/email-log; the same
 * collection is written by `email_utils.send_email` and by the
 * forgot-password endpoint's no-account branch.
 */
const KIND_LABEL = {
  password_reset: 'Password reset',
  invite: 'Invite',
  access_request: 'Access request',
  access_ending: 'Access ending',
  extension_payment: 'Extension payment',
  access_extended: 'Access extended',
  board_update: 'Board update',
  generic_template: 'Workspace email',
  backup_alert: 'Backup alert',
  support_message: 'Support message',
};

const STATUS_COLOR = {
  sent:       { dot: '#8FA37A', text: 'var(--kahla-charcoal)' },
  error:      { dot: '#C75050', text: '#7a3a3a' },
  no_account: { dot: '#c17c3e', text: 'var(--kahla-text-light)' },
  skipped:    { dot: '#A69888', text: 'var(--kahla-text-light)' },
};

const STATUS_LABEL = {
  sent: 'Delivered',
  error: 'Failed',
  no_account: 'No account',
  skipped: 'Skipped',
};

const AdminEmailLog = () => {
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({ total: 0, errors_24h: 0 });
  const [filterKind, setFilterKind] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await emailLogAPI.list({ kind: filterKind, status: filterStatus, limit: 200 });
      setEntries(res.data?.entries || []);
      setCounts(res.data?.counts || { total: 0, errors_24h: 0 });
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterKind, filterStatus]);

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch (e) { return iso; }
  };

  return (
    <div data-testid="admin-email-log" style={{ maxWidth: 1100 }}>
      <p style={{
        fontFamily: 'Cormorant Garamond, Playfair Display, serif',
        fontStyle: 'italic',
        fontSize: 15, lineHeight: 1.7,
        color: 'var(--kahla-text-light)',
        margin: '0 0 28px',
        maxWidth: 620,
      }}>
        Every outbound email is recorded here — password resets, invites, alerts.
        Includes the Resend response code on failure so you can see exactly
        what happened.
      </p>

      {/* Quiet summary row */}
      <div style={{
        display: 'flex', gap: 56, alignItems: 'baseline',
        marginBottom: 32,
      }}>
        <Stat label="Total recorded" value={counts.total} />
        <Stat label="Errors in last 24h" value={counts.errors_24h} accent={counts.errors_24h > 0 ? '#C75050' : 'var(--kahla-charcoal)'} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        <FilterSelect
          label="Kind"
          value={filterKind}
          onChange={setFilterKind}
          options={[['', 'All kinds'], ...Object.entries(KIND_LABEL)]}
          testId="email-log-filter-kind"
        />
        <FilterSelect
          label="Status"
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            ['', 'All statuses'],
            ['sent', 'Delivered'],
            ['error', 'Failed'],
            ['no_account', 'No account'],
            ['skipped', 'Skipped'],
          ]}
          testId="email-log-filter-status"
        />
        <button
          type="button"
          onClick={load}
          data-testid="email-log-refresh"
          style={{
            background: 'transparent', border: '1px solid var(--kahla-border)',
            padding: '8px 18px', fontFamily: 'inherit',
            fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'var(--kahla-text-light)', cursor: 'pointer',
          }}
        >Refresh</button>
      </div>

      {loading ? (
        <p style={{ fontStyle: 'italic', color: 'var(--kahla-text-light)' }}>Loading…</p>
      ) : entries.length === 0 ? (
        <p data-testid="email-log-empty" style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic', fontSize: 15, color: 'var(--kahla-text-light)',
        }}>
          No email events yet for the chosen filters.
        </p>
      ) : (
        <div data-testid="email-log-list" style={{ borderTop: '1px solid var(--kahla-border)' }}>
          {entries.map((e, i) => {
            const color = STATUS_COLOR[e.status] || STATUS_COLOR.skipped;
            return (
              <div
                key={i}
                data-testid={`email-log-row-${i}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 130px 220px minmax(0, 1fr) 120px',
                  gap: 16,
                  alignItems: 'center',
                  padding: '14px 0',
                  borderBottom: '1px solid var(--kahla-border)',
                  fontSize: 13,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span aria-hidden="true" style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: color.dot, flexShrink: 0,
                  }} />
                  <span style={{ color: color.text, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    {STATUS_LABEL[e.status] || e.status}
                  </span>
                </div>
                <div style={{ color: 'var(--kahla-text-light)', fontSize: 11, letterSpacing: '0.06em' }}>
                  {KIND_LABEL[e.kind] || e.kind}
                </div>
                <div style={{ color: 'var(--kahla-charcoal)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.to}>
                  {e.to || '—'}
                </div>
                <div style={{ color: 'var(--kahla-text-light)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.error || e.resend_id || e.subject}>
                  {e.status === 'error'
                    ? <span style={{ color: '#7a3a3a' }}>{e.error || 'unknown error'}</span>
                    : e.resend_id
                      ? <span style={{ fontFamily: 'Menlo, monospace', fontSize: 11 }}>{e.resend_id.slice(0, 12)}…</span>
                      : <span style={{ fontStyle: 'italic' }}>{e.subject}</span>}
                </div>
                <div style={{ color: 'var(--kahla-text-light)', fontSize: 11, textAlign: 'right' }}>
                  {formatTime(e.created_at)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quiet QA widget: render any transactional email template inline. */}
      <EmailTemplatePreview />
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div>
    <div style={{
      fontFamily: 'Playfair Display, serif',
      fontSize: 36, fontWeight: 400,
      color: accent || 'var(--kahla-charcoal)',
      lineHeight: 1.05,
    }}>{value}</div>
    <div style={{
      fontSize: 10, letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: 'var(--kahla-text-light)', marginTop: 8,
    }}>{label}</div>
  </div>
);

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <span style={{
      fontSize: 10, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'var(--kahla-text-light)',
    }}>{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{
        padding: '7px 10px', fontFamily: 'inherit',
        fontSize: 12,
        background: '#fff', border: '1px solid var(--kahla-border)',
        color: 'var(--kahla-charcoal)',
      }}
    >
      {options.map(([v, lbl]) => <option key={v || 'all'} value={v}>{lbl}</option>)}
    </select>
  </label>
);

export default AdminEmailLog;
